import React, { useEffect, useState } from 'react';
import { useWiki } from '../../contexts/WikiContext';
import { getEmptySearchQueries, getTopPages, getStalePages, deleteSearchLog, deleteSearchLogs } from '../../api/wikiApi';
import { BarChart2, Search, TrendingUp, Clock, Eye, Trash2 } from 'lucide-react';
import { useWikiAdminAuth } from './WikiAdminPanel';
import styles from './WikiDashboard.module.css';
import pagesStyles from './WikiPagesManager.module.css';

const STALE_DAYS = 7;
const TOP_LIMIT = 10;

const WikiAdminAnalytics = () => {
  const { getAllPages, loadPages, loading: wikiLoading } = useWiki();
  const [searchLogsPage, setSearchLogsPage] = useState(null);
  const [searchPage, setSearchPage] = useState(0);
  const [searchSize] = useState(50);
  const [minSearchCount, setMinSearchCount] = useState(1);
  const [topPages, setTopPages] = useState([]);
  const [stalePages, setStalePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { onAuthError } = useWikiAdminAuth();
  const [deleteError, setDeleteError] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteBefore, setDeleteBefore] = useState('');

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    if (wikiLoading) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      const pages = getAllPages();

      try {
        const [fromApiEmptyPage, fromApiTop, fromApiStale] = await Promise.all([
          getEmptySearchQueries({ page: searchPage, size: searchSize, minSearchCount }).catch((err) => {
            if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
              onAuthError(err);
            }
            return null;
          }),
          getTopPages(TOP_LIMIT).catch((err) => {
            if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
              onAuthError(err);
            }
            return null;
          }),
          getStalePages(STALE_DAYS).catch((err) => {
            if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
              onAuthError(err);
            }
            return null;
          }),
        ]);

        if (cancelled) return;

        if (fromApiEmptyPage && typeof fromApiEmptyPage === 'object') {
          setSearchLogsPage(fromApiEmptyPage);
        } else {
          setSearchLogsPage({
            content: [],
            totalPages: 0,
            totalElements: 0,
            size: searchSize,
            number: searchPage,
            first: true,
            last: true,
          });
        }

        setTopPages(
          Array.isArray(fromApiTop) && fromApiTop.length > 0
            ? fromApiTop
            : [...pages].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, TOP_LIMIT)
        );

        const threshold = new Date();
        threshold.setDate(threshold.getDate() - STALE_DAYS);
        setStalePages(
          Array.isArray(fromApiStale) && fromApiStale.length > 0
            ? fromApiStale
            : [...pages]
                .filter((p) => {
                  const updated = p.updatedAt ? new Date(p.updatedAt) : null;
                  return updated && updated < threshold;
                })
                .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [getAllPages, wikiLoading, searchPage, searchSize, minSearchCount]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const emptyList = Array.isArray(searchLogsPage?.content) ? searchLogsPage.content : [];
  const topList = Array.isArray(topPages) ? topPages : [];
  const staleList = Array.isArray(stalePages) ? stalePages : [];

  return (
    <div className={styles.dashboard}>
      <h1 className={styles.title}>
        <BarChart2 size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        Аналитика
      </h1>

      {loading ? (
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Загрузка...</p>
      ) : (
        <>
          <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
            <div className={styles.sectionHeader}>
              <Search size={20} />
              <h2 className={styles.sectionTitle}>Пустые поисковые запросы</h2>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>
              Сколько раз пользователи искали запросы, по которым поиск не нашёл ни одной статьи.
            </p>
            {deleteError && (
              <p style={{ fontSize: '0.85rem', color: '#ff6b6b', marginBottom: '0.5rem' }}>
                {deleteError}
              </p>
            )}
            <div className={pagesStyles.bulkToolbar} style={{ justifyContent: 'space-between' }}>
              <label style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                Минимум сессий:
                <select
                  value={minSearchCount}
                  onChange={(e) => { setSearchPage(0); setMinSearchCount(Number(e.target.value) || 1); }}
                  style={{
                    marginLeft: '0.5rem',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    padding: '0.25rem 0.5rem',
                  }}
                >
                  {[1, 3, 5, 10].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              {searchLogsPage && (
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
                  Страница {searchLogsPage.number + 1} из {Math.max(1, searchLogsPage.totalPages || 1)}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={async () => {
                    setDeleteError('');
                    setDeletingAll(true);
                    try {
                      await deleteSearchLogs();
                      setSearchPage(0);
                      const refreshed = await getEmptySearchQueries({
                        page: 0,
                        size: searchSize,
                        minSearchCount,
                      });
                      if (refreshed) setSearchLogsPage(refreshed);
                    } catch (err) {
                      if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
                        onAuthError(err);
                      } else {
                        setDeleteError(err.message || 'Не удалось удалить логи поиска');
                      }
                    } finally {
                      setDeletingAll(false);
                    }
                  }}
                  disabled={deletingAll || (searchLogsPage?.totalElements ?? 0) === 0}
                  className={pagesStyles.dangerButton}
                >
                  {deletingAll ? 'Очистка...' : 'Очистить всё'}
                </button>
                <label style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
                  Удалить до даты:
                  <input
                    type="date"
                    value={deleteBefore}
                    onChange={(e) => setDeleteBefore(e.target.value)}
                    style={{
                      marginLeft: '0.5rem',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      padding: '0.25rem 0.5rem',
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={async () => {
                    if (!deleteBefore) return;
                    setDeleteError('');
                    setDeletingAll(true);
                    try {
                      const beforeIso = new Date(deleteBefore).toISOString();
                      await deleteSearchLogs(beforeIso);
                      setSearchPage(0);
                      const refreshed = await getEmptySearchQueries({
                        page: 0,
                        size: searchSize,
                        minSearchCount,
                      });
                      if (refreshed) setSearchLogsPage(refreshed);
                    } catch (err) {
                      if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
                        onAuthError(err);
                      } else {
                        setDeleteError(err.message || 'Не удалось удалить логи до даты');
                      }
                    } finally {
                      setDeletingAll(false);
                    }
                  }}
                  disabled={deletingAll || !deleteBefore}
                  className={pagesStyles.secondaryButton}
                >
                  Удалить до даты
                </button>
              </div>
            </div>
            {emptyList.length > 0 ? (
              <div className={pagesStyles.tableWrapper}>
                <table className={pagesStyles.table}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Запрос</th>
                      <th style={{ textAlign: 'right' }}>Поисков</th>
                      <th style={{ textAlign: 'right' }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emptyList.map((item) => (
                      <tr key={item.id ?? `${item.query}-${item.updatedAt || item.timestamp || ''}`}>
                        <td className={pagesStyles.titleCell}>
                          <span style={{ fontWeight: 500 }}>&quot;{item.query}&quot;</span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                          {item.searchCount ?? 1}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div className={pagesStyles.actions} style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                setDeleteError('');
                                setDeletingId(item.id ?? null);
                                try {
                                  if (!item.id) return;
                                  await deleteSearchLog(item.id);
                                  const refreshed = await getEmptySearchQueries({
                                    page: searchPage,
                                    size: searchSize,
                                    minSearchCount,
                                  });
                                  if (refreshed) setSearchLogsPage(refreshed);
                                } catch (err) {
                                  if ((err?.status === 401 || err?.status === 403 || err?.isAdminForbidden) && onAuthError) {
                                    onAuthError(err);
                                  } else {
                                    setDeleteError(err.message || 'Не удалось удалить запись лога');
                                  }
                                } finally {
                                  setDeletingId(null);
                                }
                              }}
                              disabled={!item.id || deletingId === item.id}
                              title="Удалить запись"
                              className={`${pagesStyles.actionButton} ${pagesStyles.deleteButton}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {searchLogsPage && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => searchLogsPage && !searchLogsPage.first && setSearchPage((p) => Math.max(0, p - 1))}
                      disabled={!searchLogsPage || searchLogsPage.first}
                      style={{
                        padding: '0.35rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.25)',
                        background: 'transparent',
                        color: '#fff',
                        cursor: searchLogsPage && !searchLogsPage.first ? 'pointer' : 'default',
                        opacity: searchLogsPage && !searchLogsPage.first ? 1 : 0.5,
                      }}
                    >
                      Назад
                    </button>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                      Всего запросов: {searchLogsPage.totalElements ?? emptyList.length}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        searchLogsPage &&
                        !searchLogsPage.last &&
                        setSearchPage((p) => p + 1)
                      }
                      disabled={!searchLogsPage || searchLogsPage.last}
                      style={{
                        padding: '0.35rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.25)',
                        background: 'transparent',
                        color: '#fff',
                        cursor: searchLogsPage && !searchLogsPage.last ? 'pointer' : 'default',
                        opacity: searchLogsPage && !searchLogsPage.last ? 1 : 0.5,
                      }}
                    >
                      Вперёд
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Нет данных о пустых запросах</p>
              </div>
            )}
          </div>

          <div className={styles.section} style={{ marginBottom: '1.5rem' }}>
            <div className={styles.sectionHeader}>
              <TrendingUp size={20} />
              <h2 className={styles.sectionTitle}>Самые популярные страницы</h2>
            </div>
            {topList.length > 0 ? (
              <div className={styles.pagesList}>
                {topList.map((page, index) => (
                  <div key={page.id || index} className={styles.pageItem}>
                    <div className={styles.pageRank}>{index + 1}</div>
                    <div className={styles.pageInfo}>
                      <div className={styles.pageName}>{page.title || page.name || '—'}</div>
                      <div className={styles.pageMeta}>
                        Обновлено: {formatDate(page.updatedAt)}
                      </div>
                    </div>
                    <div className={styles.pageViews}>
                      <Eye size={16} />
                      <span>{page.views ?? 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Нет данных о просмотрах</p>
              </div>
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Clock size={20} />
              <h2 className={styles.sectionTitle}>Страницы, давно не обновлявшиеся</h2>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
              Старше {STALE_DAYS} дней.
            </p>
            {staleList.length > 0 ? (
              <div className={styles.pagesList}>
                {staleList.map((page, index) => (
                  <div key={page.id || index} className={styles.pageItem}>
                    <div className={styles.pageInfo} style={{ flex: 1 }}>
                      <div className={styles.pageName}>{page.title || page.name || '—'}</div>
                      <div className={styles.pageMeta}>
                        Обновлено: {formatDate(page.updatedAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Нет страниц старше {STALE_DAYS} дней</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WikiAdminAnalytics;
